import type { DocumentStatus, ObligationStatus, ComplianceScore } from '../../api/services/document.service';

// The status vocabulary, with no React in it — the half worth testing.
// Deliberately identical to the mobile copy
// (TopOps-mobile-v1/src/components/files/vaultStatus.ts): the same document
// must not read as "Expired" on a phone and "Overdue" on a laptop.
//
// The rule that matters most: MISSING IS NOT EXPIRED.
//   expired  → a thing that went wrong. Solid pill, tinted row.
//   missing  → an absence. Outline pill, no file glyph.
// "Dana has no MVR on record" and "Dana's MVR lapsed" need completely
// different reactions, so they must never render alike.

export type VaultTone = 'current' | 'soon' | 'expired' | 'missing' | 'archived';

export function toneForDocument(status: DocumentStatus): VaultTone {
  switch (status) {
    case 'expiring_soon': return 'soon';
    case 'expired': return 'expired';
    case 'archived': return 'archived';
    default: return 'current';
  }
}

export function toneForObligation(status: ObligationStatus): VaultTone {
  switch (status) {
    case 'due_soon': return 'soon';
    case 'overdue':
    case 'lapsed': return 'expired';
    case 'missing': return 'missing';
    default: return 'current';
  }
}

export function labelForObligation(status: ObligationStatus): string {
  switch (status) {
    case 'due_soon': return 'Expiring Soon';
    case 'overdue': return 'Expired';
    // "Lapsed" is engine vocabulary. To the person looking at the screen it
    // is simply expired — the difference is only that we have stopped
    // nagging about it, which is not their problem to understand.
    case 'lapsed': return 'Expired';
    case 'missing': return 'Missing';
    default: return 'Current';
  }
}

export function labelForDocument(status: DocumentStatus): string {
  switch (status) {
    case 'expiring_soon': return 'Expiring Soon';
    case 'expired': return 'Expired';
    case 'archived': return 'Superseded';
    default: return 'Current';
  }
}

// MUI palette keys, so a pill and a progress bar agree without either one
// hard-coding a hex value.
export function colorForTone(tone: VaultTone): 'success' | 'warning' | 'error' | 'inherit' {
  switch (tone) {
    case 'soon': return 'warning';
    case 'expired':
    case 'missing': return 'error';
    case 'archived': return 'inherit';
    default: return 'success';
  }
}

// A coverage score is only meaningful against a real denominator. `null`
// means this owner has no obligations yet — NOT that they are fully
// compliant — and every caller must render that as "Not tracked".
export function describeScore(score: ComplianceScore | null): string {
  if (!score || score.total === 0) return 'Not tracked';
  return `${score.percent}% · ${score.satisfied} of ${score.total}`;
}

// The absolute date is what someone types into a renewal form; the relative
// distance is what tells them whether to care today.
//
// The relative half is only added inside a window either side of now.
// "Jan 29, 2028 · in 516 days" is a true sentence that helps nobody — it
// pushes the date out of the column and buries the two documents that
// really are due next. Past dates keep their distance for a year, because
// "expired 33 days ago" and "expired 400 days ago" are different problems.
const SOON_WINDOW_DAYS = 90;
const RECENT_PAST_DAYS = 365;

export function describeExpiry(expiresAt?: string | null, prefixWhenPast = 'Expired'): string | null {
  if (!expiresAt) return null;
  const due = new Date(expiresAt);
  if (Number.isNaN(due.getTime())) return null;

  const date = due.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const days = Math.round((due.getTime() - Date.now()) / 86_400_000);

  if (days < 0) {
    const past = Math.abs(days);
    if (past > RECENT_PAST_DAYS) return `${prefixWhenPast} ${date}`;
    return `${prefixWhenPast} ${date} · ${past === 1 ? '1 day' : `${past} days`} ago`;
  }
  if (days === 0) return `${date} · today`;
  if (days === 1) return `${date} · tomorrow`;
  if (days > SOON_WINDOW_DAYS) return date;
  return `${date} · in ${days} days`;
}

export function formatBytes(bytes?: number): string {
  if (!bytes || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export const CATEGORY_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  insurance: 'Insurance',
  vehicle: 'Vehicles',
  accident: 'Accidents',
  vedr: 'VEDR',
  isp_agreement: 'ISP Agreement',
  vendor: 'Vendors',
  handbook: 'Handbook',
  job_description: 'Job Descriptions',
  invoice: 'Invoices',
  vehicle_list: 'Vehicle List',
  company: 'Company',
  operations: 'Operations',
  report: 'Reports',
  payroll: 'Payroll',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] || category;
}
